// @redux-policy: transient-display-cache
// validationThunks.js (loadValidationQueue, loadValidationHistory, etc.).
// All writes go through API endpoints. No localStorage persistence.
// Reviewed: 2026-02-19. See REDUX_VIOLATIONS.md V-P0-003.
import { createSlice, createSelector } from '@reduxjs/toolkit';

/**
 * Statuts de validation pour les données soumises par les customers
 */
export const VALIDATION_STATUS = {
  DRAFT: 'draft',                    // Brouillon (non soumis)
  PENDING: 'pending',                // En attente de validation
  IN_REVIEW: 'in_review',           // En cours de révision
  APPROVED: 'approved',              // Approuvé
  REJECTED: 'rejected',              // Rejeté
  REVISION_REQUIRED: 'revision_required', // Révision requise
  COMPLETED: 'completed'             // Complété et vérifié
};

/**
 * Types d'entités validables
 */
export const VALIDATION_ENTITY_TYPES = {
  COMPANY: 'company',
  TRADE_PARTNER: 'trade_partner',
  DOCUMENT: 'document',
  BANK_ACCOUNT: 'bank_account',
  INVOICE: 'invoice',
  INVOICE_FINANCING: 'invoice_financing',
  TRADE_DOCUMENTS: 'trade_documents',
  CONTACT: 'contact',
  RISK_ANALYSIS: 'risk_analysis'
};

const initialState = {
  // Queue de validation (pour platform users)
  validationQueue: [],

  // Historique des validations
  validationHistory: [],

  // Filtres
  filters: {
    status: 'all',
    entityType: 'all',
    assignedTo: 'all',
    searchTerm: ''
  },

  // Statistiques
  stats: {
    pending: 0,
    inReview: 0,
    approved: 0,
    rejected: 0,
    revisionRequired: 0
  },

  loading: false,
  error: null
};

// Helper function to convert status to stats key
const statusToStatsKey = (status) => {
  const mapping = {
    [VALIDATION_STATUS.PENDING]: 'pending',
    [VALIDATION_STATUS.IN_REVIEW]: 'inReview',
    [VALIDATION_STATUS.APPROVED]: 'approved',
    [VALIDATION_STATUS.REJECTED]: 'rejected',
    [VALIDATION_STATUS.REVISION_REQUIRED]: 'revisionRequired',
    [VALIDATION_STATUS.COMPLETED]: 'approved', // Count completed as approved
    [VALIDATION_STATUS.DRAFT]: 'pending' // Count draft as pending
  };
  return mapping[status] || 'pending';
};

const validationSlice = createSlice({
  name: 'validation',
  initialState,
  reducers: {
    // Ajouter un item à la queue de validation
    addToValidationQueue: (state, action) => {
      const item = {
        id: action.payload.id || Date.now().toString(),
        entityType: action.payload.entityType,
        entityId: action.payload.entityId,
        companyId: action.payload.companyId,
        customerId: action.payload.customerId,
        customerName: action.payload.customerName,
        status: VALIDATION_STATUS.PENDING,
        submittedAt: new Date().toISOString(),
        submittedBy: action.payload.submittedBy,
        assignedTo: action.payload.assignedTo || null,
        data: action.payload.data,
        previousData: action.payload.previousData || null,
        changes: action.payload.changes || [],
        comments: []
      };
      state.validationQueue.push(item);
      state.stats.pending++;
    },

    // Mettre à jour le statut de validation
    updateValidationStatus: (state, action) => {
      const { id, status, comment, reviewedBy } = action.payload;

      const item = state.validationQueue.find(i => i.id === id);
      if (item) {
        const previousStatus = item.status;
        item.status = status;
        item.updatedAt = new Date().toISOString();
        item.reviewedBy = reviewedBy;

        if (comment) {
          item.comments.push({
            text: comment,
            author: reviewedBy,
            timestamp: new Date().toISOString(),
            statusChange: { from: previousStatus, to: status }
          });
        }

        // Mettre à jour les stats
        if (previousStatus !== status) {
          const prevKey = statusToStatsKey(previousStatus);
          const newKey = statusToStatsKey(status);
          if (state.stats[prevKey] !== undefined) {
            state.stats[prevKey]--;
          }
          if (state.stats[newKey] !== undefined) {
            state.stats[newKey]++;
          }
        }

        // Si approuvé ou rejeté, ajouter à l'historique et retirer de la queue
        if (status === VALIDATION_STATUS.APPROVED ||
            status === VALIDATION_STATUS.REJECTED ||
            status === VALIDATION_STATUS.COMPLETED) {
          state.validationHistory.push({
            ...item,
            completedAt: new Date().toISOString()
          });
          state.validationQueue = state.validationQueue.filter(i => i.id !== id);
        }
      }
    },

    // Assigner un validateur
    assignValidator: (state, action) => {
      const { id, userId, userName } = action.payload;
      const item = state.validationQueue.find(i => i.id === id);
      if (item) {
        item.assignedTo = userId;
        item.assignedToName = userName;
        item.assignedAt = new Date().toISOString();

        if (item.status === VALIDATION_STATUS.PENDING) {
          item.status = VALIDATION_STATUS.IN_REVIEW;
          state.stats.pending--;
          state.stats.inReview++;
        }
      }
    },

    // Ajouter un commentaire
    addComment: (state, action) => {
      const { id, comment, author } = action.payload;
      const item = state.validationQueue.find(i => i.id === id);
      if (item) {
        item.comments.push({
          text: comment,
          author,
          timestamp: new Date().toISOString()
        });
      }
    },

    removeFromValidationQueue: (state, action) => {
      const id = action.payload;
      const item = state.validationQueue.find((entry) => entry.id === id);
      if (item) {
        const statusKey = statusToStatsKey(item.status);
        if (state.stats[statusKey] !== undefined && state.stats[statusKey] > 0) {
          state.stats[statusKey]--;
        }
      }
      state.validationQueue = state.validationQueue.filter((item) => item.id !== id);
    },

    // Charger la queue de validation
    setValidationQueue: (state, action) => {
      state.validationQueue = action.payload;

      // Recalculer les stats
      state.stats = {
        pending: 0,
        inReview: 0,
        approved: 0,
        rejected: 0,
        revisionRequired: 0
      };

      action.payload.forEach(item => {
        const statusKey = statusToStatsKey(item.status);
        if (state.stats[statusKey] !== undefined) {
          state.stats[statusKey]++;
        }
      });
    },

    // Charger l'historique
    setValidationHistory: (state, action) => {
      state.validationHistory = action.payload;
    },

    // Mettre à jour les filtres
    setValidationFilter: (state, action) => {
      state.filters = {
        ...state.filters,
        ...action.payload
      };
    },

    // Set loading
    setValidationLoading: (state, action) => {
      state.loading = action.payload;
    },

    // Set error
    setValidationError: (state, action) => {
      state.error = action.payload;
    },

    // Clear error
    clearValidationError: (state) => {
      state.error = null;
    }
  }
});

// Selectors
export const selectValidationQueue = (state) => state.validation.validationQueue;

const selectValidationFilters = (state) => state.validation.filters;

export const selectFilteredValidationQueue = createSelector(
  [selectValidationQueue, selectValidationFilters],
  (validationQueue, filters) => validationQueue.filter((item) => {
    // Normalize status for comparison (handle both 'pending' and 'PENDING', 'in_review' and 'IN_REVIEW')
    const normalizeStatus = (status) => {
      if (!status) return '';
      return status.toString().toLowerCase().replace(/_/g, '_');
    };

    // Filter by status
    if (filters.status !== 'all') {
      const itemStatus = normalizeStatus(item.status);
      const filterStatus = normalizeStatus(filters.status);
      if (itemStatus !== filterStatus) {
        return false;
      }
    }

    // Filter by entity type
    if (filters.entityType !== 'all' && item.entityType !== filters.entityType) {
      return false;
    }

    // Filter by assigned user
    if (filters.assignedTo !== 'all') {
      if (filters.assignedTo === 'unassigned') {
        // Show only unassigned items
        if (item.assignedTo !== null && item.assignedTo !== undefined) {
          return false;
        }
      } else {
        // Show items assigned to specific user (compare as strings to handle both number and string IDs)
        const itemAssignedTo = item.assignedTo?.toString();
        const filterAssignedTo = filters.assignedTo?.toString();
        if (itemAssignedTo !== filterAssignedTo) {
          return false;
        }
      }
    }

    // Filter by search term
    if (filters.searchTerm) {
      const searchLower = filters.searchTerm.toLowerCase();
      const customerMatch = item.customerName?.toLowerCase().includes(searchLower);
      const entityMatch = item.entityType?.toLowerCase().includes(searchLower);

      if (!customerMatch && !entityMatch) {
        return false;
      }
    }

    return true;
  })
);

export const selectValidationHistory = (state) => state.validation.validationHistory;

export const selectValidationStats = (state) => state.validation.stats;

export const selectMyValidationItems = (userId) => (state) => {
  return state.validation.validationQueue.filter(item => item.assignedTo === userId);
};

export const selectPendingValidations = (state) => {
  return state.validation.validationQueue.filter(
    item => item.status === VALIDATION_STATUS.PENDING
  );
};

export const {
  addToValidationQueue,
  updateValidationStatus,
  assignValidator,
  addComment,
  removeFromValidationQueue,
  setValidationQueue,
  setValidationHistory,
  setValidationFilter,
  setValidationLoading,
  setValidationError,
  clearValidationError
} = validationSlice.actions;

export default validationSlice.reducer;
